import { lazy, Suspense } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Snowflake, ArrowRight, Package, Thermometer, Truck, Shield, Clock, Star, CheckCircle, Building2, UtensilsCrossed, Beaker, Zap } from "lucide-react";
import PublicNavbar from "@/components/PublicNavbar";
import PublicFooter from "@/components/PublicFooter";
import epsBoxImg from "@/assets/eps-box-transparent.png";
import containerImg from "@/assets/dry-ice-container-transparent.png";
import horecaImage from "@/assets/order-type-horeca.jpg";
import industrialImage from "@/assets/product-bulk-dryice.jpg";
import uspQualityImage from "@/assets/usp-quality.jpg";
import heroUniversalImage from "@/assets/hero-universal.jpg";
import branchHoreca from "@/assets/branch-horeca.jpg";
import branchPharma from "@/assets/branch-pharma.jpg";
import branchTransport from "@/assets/branch-transport.jpg";
import branchFood from "@/assets/branch-food.jpg";
import branchCleaning from "@/assets/branch-cleaning.jpg";
import branchEvents from "@/assets/branch-events.jpg";
import { useLanguage } from "@/contexts/LanguageContext";
import { useLocalePath } from "@/hooks/useLocalePath";
import { useABTest } from "@/hooks/useABTest";
import UrgencyBanner from "@/components/marketing/UrgencyBanner";
import PageMeta from "@/components/PageMeta";
import { useFunnelTracker } from "@/hooks/useFunnelTracker";

// Lazy load below-fold components to reduce initial bundle
const CustomerStories = lazy(() => import("@/components/marketing/CustomerStories"));
const NewsletterSignup = lazy(() => import("@/components/marketing/NewsletterSignup"));
const ExitIntentPopup = lazy(() => import("@/components/marketing/ExitIntentPopup"));
const MobileStickyBar = lazy(() => import("@/components/marketing/MobileStickyBar"));
const WhatsAppButton = lazy(() => import("@/components/marketing/WhatsAppButton"));

const Landing = () => {
  const { t, language } = useLanguage();
  const lp = useLocalePath();
  const { variant: ctaVariant, variantConfig: ctaConfig, trackEvent: trackCta } = useABTest("hero_cta");
  const { variantConfig: headlineConfig, trackEvent: trackHeadline } = useABTest("hero_headline");
  useFunnelTracker("landing_visit");

  const heroTitle = (headlineConfig && (language === "en" ? headlineConfig.title_en : headlineConfig.title_nl)) || t("landing.heroTitle");
  const rawHeroSubtitle = (headlineConfig && (language === "en" ? headlineConfig.subtitle_en : headlineConfig.subtitle_nl)) || t("landing.heroSubtitle");
  const heroSubtitle = rawHeroSubtitle.replace(/15:00/g, "12:00");

  const steps = [
    { icon: Package, step: "01", title: t("landing.step1Title"), desc: t("landing.step1Desc") },
    { icon: Thermometer, step: "02", title: t("landing.step2Title"), desc: t("landing.step2Desc") },
    { icon: Truck, step: "03", title: t("landing.step3Title"), desc: t("landing.step3Desc") },
  ];

  const branches = [
    { img: branchHoreca, icon: UtensilsCrossed, title: t("landing.branchHoreca"), desc: t("landing.branchHorecaDesc"), link: lp("/oplossingen/horeca-catering") },
    { img: branchPharma, icon: Beaker, title: t("landing.branchPharma"), desc: t("landing.branchPharmaDesc"), link: lp("/oplossingen/medisch-farmaceutisch") },
    { img: branchTransport, icon: Truck, title: t("landing.branchTransport"), desc: t("landing.branchTransportDesc"), link: lp("/oplossingen/transport-logistiek") },
    { img: branchFood, icon: Building2, title: t("landing.branchFood"), desc: t("landing.branchFoodDesc"), link: lp("/oplossingen/voedselindustrie") },
    { img: branchCleaning, icon: Zap, title: t("landing.branchCleaning"), desc: t("landing.branchCleaningDesc"), link: lp("/oplossingen/industrieel-reinigen") },
    { img: branchEvents, icon: Star, title: t("landing.branchEvents"), desc: t("landing.branchEventsDesc"), link: lp("/oplossingen/evenementen") },
  ];

  const usps = [
    { icon: CheckCircle, title: t("landing.usp1Title"), desc: t("landing.usp1Desc") },
    { icon: Truck, title: t("landing.usp2Title"), desc: t("landing.usp2Desc") },
    { icon: Clock, title: t("landing.usp3Title"), desc: t("landing.usp3Desc") },
    { icon: Shield, title: t("landing.usp4Title"), desc: t("landing.usp4Desc") },
  ];

  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Dutch DryIce",
    url: "https://dutchdryice.com",
    logo: "https://dutchdryice.com/favicon.svg",
    description: t("landing.heroSubtitle"),
    address: {
      "@type": "PostalAddress",
      addressCountry: "NL",
    },
    contactPoint: {
      "@type": "ContactPoint",
      contactType: "customer service",
      email: "info@dutchdryice.com",
    },
  };

  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name: "Dutch DryIce",
    url: "https://dutchdryice.com",
    logo: "https://dutchdryice.com/favicon.svg",
    description: "Leverancier van NEN-gecertificeerd droogijs in Nederland. Volgende dag levering door heel Nederland.",
    email: "info@dutchdryice.com",
    address: {
      "@type": "PostalAddress",
      addressCountry: "NL",
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: "52.3676",
      longitude: "4.9041",
    },
    areaServed: {
      "@type": "Country",
      name: "Netherlands",
    },
    priceRange: "€€",
    openingHoursSpecification: {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
      opens: "00:00",
      closes: "23:59",
    },
    sameAs: [],
  };

  const productSchemaEps = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: t("landing.epsTitle"),
    description: t("landing.epsDesc"),
    brand: { "@type": "Brand", name: "Dutch DryIce" },
    offers: {
      "@type": "Offer",
      priceCurrency: "EUR",
      availability: "https://schema.org/InStock",
      url: "https://dutchdryice.com/bestellen/eps",
    },
  };

  const productSchemaContainer = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: t("landing.containerTitle"),
    description: t("landing.containerDesc"),
    brand: { "@type": "Brand", name: "Dutch DryIce" },
    offers: {
      "@type": "Offer",
      priceCurrency: "EUR",
      availability: "https://schema.org/InStock",
      url: "https://dutchdryice.com/bestellen/container",
    },
  };

  return (
    <div className="min-h-screen bg-background">
      <PageMeta
        title={t("landing.heroTitle")}
        description={
          language === "en"
            ? "NEN-certified dry ice at the lowest price in the Netherlands. From 6 kg EPS box to pallet. Order before 12:00, on site next business day."
            : "NEN-gecertificeerd droogijs tegen de laagste prijs van NL. Van 6 kg EPS-box tot pallet. Bestel voor 12:00, morgen op locatie."
        }
        ogImage={heroUniversalImage}
        canonicalPath={language === "en" ? "/en" : "/"}
        jsonLd={[organizationSchema, localBusinessSchema, productSchemaEps, productSchemaContainer]}
      />
      <UrgencyBanner />
      <PublicNavbar />

      {/* Hero */}
      <main id="main-content">
      <section className="relative" aria-label="Hero">
        <div className="relative h-[360px] sm:h-[420px] md:h-[560px] w-full">
          <img src={heroUniversalImage} alt="Droogijs productie en levering" width={1920} height={1080} className="w-full h-full object-cover" fetchPriority="high" decoding="async" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/60 to-black/30" />

          <div className="absolute inset-0 flex items-end sm:items-center pb-8 sm:pb-0">
            <div className="container mx-auto px-4">
              <div className="max-w-2xl">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/90 text-primary-foreground text-xs sm:text-sm font-medium mb-4 md:mb-6">
                  <Snowflake className="w-3.5 h-3.5" />
                  {t("landing.badge")}
                </div>

                <h1 className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-3 md:mb-5 text-primary-foreground leading-[1.15]">
                  <span className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-primary-foreground leading-[1.15]">{heroTitle}</span>
                </h1>

                <p className="text-sm sm:text-base md:text-lg text-primary-foreground/90 max-w-xl mb-5 md:mb-8 line-clamp-3 sm:line-clamp-none">{heroSubtitle}</p>

                <div className="flex flex-wrap gap-2 sm:gap-3 mb-8 md:mb-0">
                  <Button size="default" className="sm:h-11 sm:px-6" asChild>
                    <Link to={lp("/bestellen/eps")} onClick={() => { trackCta("click"); trackHeadline("click"); }}>
                      {ctaConfig?.ctaText || t("landing.ctaPrimary")} <ArrowRight className="w-4 h-4 ml-1.5" />
                    </Link>
                  </Button>
                  <Button size="default" variant="outline" className="sm:h-11 sm:px-6 border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10 bg-transparent" asChild>
                    <Link to={lp("/contact")}>{t("landing.ctaContact")}</Link>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust bar */}
      <section className="bg-card border-b border-border">
        <div className="container mx-auto px-4 py-4 md:py-5">
          <div className="flex items-center justify-between gap-x-4 text-xs font-medium text-foreground max-w-6xl mx-auto overflow-x-auto whitespace-nowrap scrollbar-hide">
            <div className="flex items-center gap-1.5 shrink-0"><Shield className="w-3.5 h-3.5 text-primary" /> {t("landing.trustNen")}</div>
            <div className="flex items-center gap-1.5 shrink-0"><Clock className="w-3.5 h-3.5 text-primary" /> {t("landing.trustDelivery")}</div>
            <div className="flex items-center gap-1.5 shrink-0"><Truck className="w-3.5 h-3.5 text-primary" /> {t("landing.trustDedicated")}</div>
            <div className="flex items-center gap-1.5 shrink-0"><Snowflake className="w-3.5 h-3.5 text-primary" /> {t("landing.trustFresh")}</div>
            <div className="flex items-center gap-1.5 shrink-0"><CheckCircle className="w-3.5 h-3.5 text-primary" /> {t("landing.trustRate")}</div>
          </div>
        </div>
      </section>

      {/* Product Cards */}
      <section className="py-12 md:py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-8 md:mb-14">
            <h2 className="text-2xl md:text-4xl font-bold mb-2 md:mb-3">{t("landing.productsTitle")}</h2>
            <p className="text-muted-foreground text-sm md:text-base max-w-lg mx-auto">{t("landing.productsSubtitle")}</p>
          </div>

          <div className="grid md:grid-cols-2 gap-4 md:gap-8 max-w-5xl mx-auto">
            {/* EPS Box */}
            <Link to={lp("/bestellen/eps")} className="group relative rounded-2xl border border-border bg-card overflow-hidden hover:shadow-elevated transition-all duration-300">
              <div className="relative h-40 sm:h-52 md:h-60 w-full overflow-hidden">
                <img src={horecaImage} alt="EPS Box droogijs" loading="lazy" width={800} height={800} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                <div className="absolute top-3 right-3 w-12 h-12 md:w-16 md:h-16 rounded-xl bg-black/40 backdrop-blur-md border border-white/10 flex items-center justify-center shadow-lg">
                  <img src={epsBoxImg} alt="EPS Box droogijsverpakking" loading="lazy" width={512} height={512} className="w-9 h-9 md:w-12 md:h-12 object-contain drop-shadow-lg" />
                </div>
                <div className="absolute bottom-3 left-3">
                  <span className="bg-primary text-primary-foreground text-[11px] font-semibold px-2.5 py-1 rounded-full">6kg – 25kg</span>
                </div>
              </div>
              <div className="p-4 md:p-6">
                <h3 className="text-lg md:text-xl font-bold mb-1.5 md:mb-2">{t("landing.epsTitle")}</h3>
                <p className="text-xs md:text-sm text-muted-foreground mb-3 md:mb-5">
                  {t("landing.epsDesc")}
                </p>
                <ul className="space-y-1.5 md:space-y-2 mb-4 md:mb-6">
                  {[t("landing.epsFeature1"), t("landing.epsFeature2"), t("landing.epsFeature3")].map((f, i) => (
                    <li key={i} className="text-xs md:text-sm text-muted-foreground flex items-center gap-2">
                      <CheckCircle className="w-3.5 h-3.5 text-primary shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
                <Button className="w-full" size="default">
                  {t("landing.epsOrder")} <ArrowRight className="w-4 h-4 ml-1.5" />
                </Button>
              </div>
            </Link>

            {/* Container */}
            <Link to={lp("/offerte")} className="group relative rounded-2xl border border-border bg-card overflow-hidden hover:shadow-elevated transition-all duration-300">
              <div className="relative h-40 sm:h-52 md:h-60 w-full overflow-hidden">
                <img src={industrialImage} alt="Container droogijs" loading="lazy" width={800} height={800} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                <div className="absolute top-3 right-3 w-12 h-12 md:w-16 md:h-16 rounded-xl bg-black/40 backdrop-blur-md border border-white/10 flex items-center justify-center shadow-lg">
                  <img src={containerImg} alt="Droogijs container" loading="lazy" width={512} height={512} className="w-9 h-9 md:w-12 md:h-12 object-contain drop-shadow-lg" />
                </div>
                <div className="absolute bottom-3 left-3">
                  <span className="bg-primary text-primary-foreground text-[11px] font-semibold px-2.5 py-1 rounded-full">Vanaf 150kg</span>
                </div>
              </div>
              <div className="p-4 md:p-6">
                <h3 className="text-lg md:text-xl font-bold mb-1.5 md:mb-2">{t("landing.containerTitle")}</h3>
                <p className="text-xs md:text-sm text-muted-foreground mb-3 md:mb-5">
                  {t("landing.containerDesc")}
                </p>
                <ul className="space-y-1.5 md:space-y-2 mb-4 md:mb-6">
                  {[t("landing.containerFeature1"), t("landing.containerFeature2"), t("landing.containerFeature3")].map((f, i) => (
                    <li key={i} className="text-xs md:text-sm text-muted-foreground flex items-center gap-2">
                      <CheckCircle className="w-3.5 h-3.5 text-primary shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
                <Button className="w-full" size="default">
                  {t("footer.quoteRequest")} <ArrowRight className="w-4 h-4 ml-1.5" />
                </Button>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-12 md:py-20 bg-sidebar text-sidebar-foreground">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl md:text-4xl font-bold text-center mb-3 md:mb-4 text-sidebar-foreground">{t("landing.howTitle")}</h2>
          <p className="text-sidebar-foreground/60 text-center mb-8 md:mb-14 max-w-xl mx-auto text-sm md:text-base">
            {t("landing.howSubtitle")}
          </p>

          <div className="grid md:grid-cols-3 gap-8 md:gap-10 max-w-4xl mx-auto">
            {steps.map((s) => (
              <div key={s.step} className="text-center">
                <div className="text-4xl md:text-5xl font-black text-primary/30 mb-2 md:mb-3">{s.step}</div>
                <div className="w-12 h-12 md:w-14 md:h-14 rounded-2xl bg-primary/15 flex items-center justify-center mx-auto mb-3 md:mb-4">
                  <s.icon className="w-6 h-6 md:w-7 md:h-7 text-primary" />
                </div>
                <h3 className="font-bold text-base md:text-lg mb-1.5 md:mb-2 text-sidebar-foreground">{s.title}</h3>
                <p className="text-xs md:text-sm text-sidebar-foreground/60">{s.desc}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-8 md:mt-14">
            <Button size="default" className="sm:h-11 sm:px-6" asChild>
              <Link to={lp("/bestellen/eps")}>
                {t("landing.orderNow")} <ArrowRight className="w-4 h-4 ml-1.5" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Branches */}
      <section className="py-12 md:py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-8 md:mb-14">
            <h2 className="text-2xl md:text-4xl font-bold mb-2 md:mb-3">{t("landing.branchesTitle")}</h2>
            <p className="text-muted-foreground max-w-xl mx-auto text-sm md:text-base">
              {t("landing.branchesSubtitle")}
            </p>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 md:gap-6 max-w-6xl mx-auto">
            {branches.map((branch) => (
              <Link to={branch.link} key={branch.title} className="rounded-xl md:rounded-2xl border border-border bg-card overflow-hidden hover:shadow-elevated transition-all duration-300 group">
                <div className="relative h-28 sm:h-36 md:h-44 w-full overflow-hidden">
                  <img src={branch.img} alt={branch.title} loading="lazy" width={768} height={512} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                  <div className="absolute bottom-2 left-2 md:bottom-3 md:left-3">
                    <div className="flex items-center gap-1.5 bg-card/90 backdrop-blur-sm rounded-full px-2 py-1 md:px-3 md:py-1.5">
                      <branch.icon className="w-3 h-3 md:w-4 md:h-4 text-primary" />
                      <span className="text-[10px] md:text-xs font-semibold text-foreground">{branch.title}</span>
                    </div>
                  </div>
                </div>
                <div className="p-3 md:p-5">
                  <p className="text-[11px] md:text-sm text-muted-foreground line-clamp-2">{branch.desc}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* USPs */}
      <section className="py-12 md:py-20 bg-card border-y border-border">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-8 md:gap-14 max-w-5xl mx-auto items-center">
            <div>
              <h2 className="text-2xl md:text-4xl font-bold mb-5 md:mb-8">{t("landing.whyTitle")}</h2>
              <div className="space-y-4 md:space-y-6">
                {usps.map((usp) => (
                  <div key={usp.title} className="flex gap-3 md:gap-4">
                    <div className="w-9 h-9 md:w-11 md:h-11 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                      <usp.icon className="w-4 h-4 md:w-5 md:h-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-bold text-sm md:text-base mb-0.5 md:mb-1">{usp.title}</h3>
                      <p className="text-xs md:text-sm text-muted-foreground">{usp.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-elevated">
              <img src={uspQualityImage} alt="Droogijs kwaliteitscontrole" loading="lazy" width={1024} height={768} className="w-full h-64 md:h-96 object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* Customer Stories */}
      <Suspense fallback={null}>
        <CustomerStories />
      </Suspense>

      {/* Newsletter */}
      <Suspense fallback={null}>
        <NewsletterSignup />
      </Suspense>

      {/* CTA Banner */}
      <section className="py-12 md:py-20">
        <div className="container mx-auto px-4">
          <div className="rounded-2xl md:rounded-3xl bg-sidebar p-8 md:p-16 text-center">
            <h2 className="text-2xl md:text-4xl font-bold mb-3 md:mb-4 text-sidebar-foreground">{t("landing.ctaTitle")}</h2>
            <p className="text-sidebar-foreground/60 max-w-lg mx-auto mb-6 md:mb-10 text-sm md:text-base">
              {t("landing.ctaSubtitle")}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 md:gap-4">
              <Button size="default" className="w-full sm:w-auto sm:h-11 sm:px-6" asChild>
                <Link to={lp("/bestellen/eps")}>
                  {t("landing.epsOrder")} <ArrowRight className="w-4 h-4 ml-1.5" />
                </Link>
              </Button>
              <Button size="default" variant="outline" className="w-full sm:w-auto sm:h-11 sm:px-6 border-sidebar-foreground/30 text-sidebar-foreground hover:bg-sidebar-foreground/10 bg-transparent" asChild>
                <Link to={lp("/offerte")}>
                  {t("footer.quoteRequest")} <ArrowRight className="w-4 h-4 ml-1.5" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      </main>
      <PublicFooter />
      <Suspense fallback={null}>
        <WhatsAppButton />
        <MobileStickyBar />
        <ExitIntentPopup />
      </Suspense>
    </div>
  );
};

export default Landing;
